/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListUserSessionFacetsRequest,
  ListUserSessionFacetsSecurity,
} from "../models/operations/listusersessionfacets.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUserSessionFacetsQuery,
  prefetchUserSessionFacets,
  queryKeyUserSessionFacets,
  UserSessionFacetsQueryData,
} from "./userSessionFacets.core.js";
export {
  buildUserSessionFacetsQuery,
  prefetchUserSessionFacets,
  queryKeyUserSessionFacets,
  type UserSessionFacetsQueryData,
};

export type UserSessionFacetsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listFacets userSessions
 *
 * @remarks
 * List available user session facet values (clients, users, servers) in the caller's project.
 */
export function useUserSessionFacets(
  request?: ListUserSessionFacetsRequest | undefined,
  security?: ListUserSessionFacetsSecurity | undefined,
  options?: QueryHookOptions<
    UserSessionFacetsQueryData,
    UserSessionFacetsQueryError
  >,
): UseQueryResult<UserSessionFacetsQueryData, UserSessionFacetsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildUserSessionFacetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listFacets userSessions
 *
 * @remarks
 * List available user session facet values (clients, users, servers) in the caller's project.
 */
export function useUserSessionFacetsSuspense(
  request?: ListUserSessionFacetsRequest | undefined,
  security?: ListUserSessionFacetsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UserSessionFacetsQueryData,
    UserSessionFacetsQueryError
  >,
): UseSuspenseQueryResult<
  UserSessionFacetsQueryData,
  UserSessionFacetsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUserSessionFacetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setUserSessionFacetsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UserSessionFacetsQueryData,
): UserSessionFacetsQueryData | undefined {
  const key = queryKeyUserSessionFacets(...queryKeyBase);

  return client.setQueryData<UserSessionFacetsQueryData>(key, data);
}

export function invalidateUserSessionFacets(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessions", "listFacets", ...queryKeyBase],
  });
}

export function invalidateAllUserSessionFacets(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessions", "listFacets"],
  });
}
